import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import * as syncApi from '../../services/sync-api.js';
import type { SyncConfig } from '../../services/sync-api.js';

const IP_HOSTNAME_REGEX = /^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}|[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*)$/;
const PORT_MIN = 1;
const PORT_MAX = 65535;

@customElement('cp-sync-settings')
export class CpSyncSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _config: SyncConfig | null = null;
  @state() private _loading = true;
  @state() private _error = '';
  @state() private _successMessage = '';
  @state() private _host = '';
  @state() private _port = '3000';
  @state() private _saving = false;
  @state() private _testing = false;

  connectedCallback(): void {
    super.connectedCallback();
    void this._loadConfig();
  }

  private async _loadConfig(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const config = await syncApi.getSyncConfig();
      this._config = config;
      if (config.masterAddress) {
        this._host = config.masterAddress;
      }
      if (config.masterPort) {
        this._port = String(config.masterPort);
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private get _isHostValid(): boolean {
    return IP_HOSTNAME_REGEX.test(this._host.trim());
  }

  private get _isPortValid(): boolean {
    const port = parseInt(this._port, 10);
    return !isNaN(port) && port >= PORT_MIN && port <= PORT_MAX;
  }

  private get _isFormValid(): boolean {
    return this._isHostValid && this._isPortValid;
  }

  private _onHostInput(e: Event): void {
    this._host = (e.target as HTMLInputElement).value;
    this._successMessage = '';
    this._error = '';
  }

  private _onPortInput(e: Event): void {
    this._port = (e.target as HTMLInputElement).value;
    this._successMessage = '';
    this._error = '';
  }

  private async _save(): Promise<void> {
    if (!this._isFormValid || this._saving) return;

    this._saving = true;
    this._error = '';
    this._successMessage = '';
    try {
      await syncApi.configureSync(this._host.trim(), parseInt(this._port, 10));
      await this._loadConfig();
      this._successMessage = 'Konfiguration gespeichert';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  private async _testConnection(): Promise<void> {
    if (!this._isFormValid || this._testing) return;

    this._testing = true;
    this._error = '';
    this._successMessage = '';
    try {
      const result = await syncApi.testConnection();
      if (result.reachable) {
        this._successMessage = 'Verbindung erfolgreich';
        await this._loadConfig();
      } else {
        this._error = result.error ?? 'Verbindung fehlgeschlagen';
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Verbindungstest fehlgeschlagen';
    } finally {
      this._testing = false;
    }
  }

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      void this._save();
    }
  }

  private _formatDate(dateStr: string | null): string {
    if (!dateStr) return 'Nie';
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private _renderStatus() {
    if (!this._config || this._config.role === 'standalone') {
      return html`<span class="cp-sync__status cp-sync__status--disconnected">Nicht konfiguriert</span>`;
    }
    if (this._config.lastTestResult === 'success') {
      return html`<span class="cp-sync__status cp-sync__status--connected">Verbunden</span>`;
    }
    if (this._config.lastTestResult === 'failure') {
      return html`<span class="cp-sync__status cp-sync__status--error">Nicht erreichbar</span>`;
    }
    return html`<span class="cp-sync__status cp-sync__status--disconnected">Nicht getestet</span>`;
  }

  render() {
    return html`
      <div class="cp-sync">
        <div class="cp-sync__header">
          <h3 class="cp-sync__title">LAN Sync</h3>
          ${this._renderStatus()}
        </div>

        ${this._error ? html`
          <div class="cp-sync__error">${this._error}</div>
        ` : nothing}

        ${this._successMessage ? html`
          <div class="cp-sync__success">${this._successMessage}</div>
        ` : nothing}

        ${this._loading ? html`
          <p class="cp-sync__loading">Konfiguration wird geladen...</p>
        ` : html`
          <div class="cp-sync__form">
            <label class="cp-sync__label" for="sync-host">Master-Adresse (IP oder Hostname)</label>
            <div class="cp-sync__input-row">
              <input
                id="sync-host"
                class="cp-sync__input ${!this._isHostValid && this._host ? 'cp-sync__input--invalid' : ''}"
                type="text"
                placeholder="192.168.1.100"
                .value=${this._host}
                @input=${this._onHostInput}
                @keydown=${this._onKeyDown}
              />
              <input
                id="sync-port"
                class="cp-sync__input cp-sync__input--port ${!this._isPortValid && this._port ? 'cp-sync__input--invalid' : ''}"
                type="number"
                min="1"
                max="65535"
                placeholder="3000"
                .value=${this._port}
                @input=${this._onPortInput}
                @keydown=${this._onKeyDown}
              />
            </div>
            <div class="cp-sync__actions">
              <button
                class="cp-sync__btn cp-sync__btn--save"
                @click=${this._save}
                ?disabled=${this._saving || !this._isFormValid}
              >${this._saving ? 'Speichere...' : 'Speichern'}</button>
              <button
                class="cp-sync__btn cp-sync__btn--test"
                @click=${this._testConnection}
                ?disabled=${this._testing || !this._isFormValid}
              >${this._testing ? 'Teste...' : 'Verbindung testen'}</button>
            </div>
          </div>

          ${this._config && this._config.role === 'follower' ? html`
            <div class="cp-sync__info">
              <div class="cp-sync__info-row">
                <span class="cp-sync__info-label">Letzter Test:</span>
                <span class="cp-sync__info-value">${this._formatDate(this._config.lastTestAt)}</span>
              </div>
              <div class="cp-sync__info-row">
                <span class="cp-sync__info-label">Letzter Sync:</span>
                <span class="cp-sync__info-value">${this._formatDate(this._config.lastSync)}</span>
              </div>
            </div>
          ` : nothing}
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-sync-settings': CpSyncSettings;
  }
}
